/**
 * Vocabulário dos comandos ao agente — puro, sem React, para o check de contrato.
 *
 * O campo é `state` (para o operador), nunca `status` (estado interno da fila).
 * A distinção que esta tabela existe para proteger: `delivered` é "escrito na
 * stream do agente", SEM confirmação. Só `executed` e `skipped` são confirmados,
 * e só eles significam que um desbloqueio devolveu a máquina à rede.
 */
import type { Tone } from '../../components/primitives/Metric.tsx'

export interface CommandStatePresentation {
  key: string
  label: string
  tone: Tone
  hint: string
}

const STATE: Record<string, Omit<CommandStatePresentation, 'key'>> = {
  pending: {
    label: 'na fila',
    tone: 'accent',
    hint: 'Aguardando entrega ao agente.',
  },
  delivered: {
    label: 'entregue · sem confirmação',
    tone: 'warn',
    hint: 'Entregue ao agente, sem confirmação.',
  },
  executed: {
    label: 'executado',
    tone: 'ok',
    hint: 'O agente confirmou a execução.',
  },
  skipped: {
    label: 'confirmado · nada a fazer',
    tone: 'ok',
    hint: 'O agente confirmou que não havia o que fazer.',
  },
  failed: {
    label: 'falhou',
    tone: 'crit',
    hint: 'O agente tentou e falhou. Veja o detalhe.',
  },
  expired: {
    label: 'expirado',
    tone: 'neutral',
    hint: 'O prazo venceu antes da confirmação.',
  },
  cancelled: {
    label: 'cancelado',
    tone: 'neutral',
    hint: 'Cancelado antes da entrega.',
  },
}

export function commandStatePresentation(state: string): CommandStatePresentation {
  const known = STATE[state]
  return known === undefined
    ? { key: state, label: state, tone: 'neutral', hint: `Estado "${state}" fora do vocabulário conhecido.` }
    : { key: state, ...known }
}

/** Ainda depende do agente: vale acompanhar (poll). */
export function isAwaitingAgent(state: string): boolean {
  return state === 'pending' || state === 'delivered'
}

/** O agente CONFIRMOU. No desbloqueio, é o único caso em que a máquina voltou à rede. */
export function isConfirmed(state: string): boolean {
  return state === 'executed' || state === 'skipped'
}

/** Intervalo do acompanhamento enquanto `isAwaitingAgent`. */
export const COMMAND_POLL_MS = 3_000

/**
 * O texto do agente para uma falha: `failure_detail` do servidor, ou o `detail`
 * do resultado reportado. `null` quando nenhum dos dois veio.
 */
export function failureText(command: {
  failure_detail: string | null
  result: { detail: string | null } | null
}): string | null {
  return command.failure_detail ?? command.result?.detail ?? null
}

/**
 * O que a tela pode AFIRMAR sobre a rede da máquina depois de um desbloqueio.
 * Escrito aqui, e não no JSX, para o check provar que `delivered` nunca vira
 * "liberada".
 */
export function releaseOutcome(state: string): {
  tone: Tone
  headline: string
  released: boolean
} {
  switch (state) {
    case 'pending':
      return { tone: 'accent', headline: 'Na fila — aguardando o agente buscar o comando.', released: false }
    case 'delivered':
      return {
        tone: 'warn',
        headline: 'Entregue ao agente, sem confirmação. A máquina continua isolada.',
        released: false,
      }
    case 'executed':
      return { tone: 'ok', headline: 'Máquina liberada: o agente confirmou o desbloqueio.', released: true }
    case 'skipped':
      return {
        tone: 'ok',
        headline: 'Máquina liberada: o agente confirmou que o host já não estava isolado.',
        released: true,
      }
    case 'failed':
      return { tone: 'crit', headline: 'O desbloqueio FALHOU. A máquina segue isolada.', released: false }
    case 'expired':
      return {
        tone: 'warn',
        headline: 'O prazo venceu sem confirmação. A máquina segue isolada.',
        released: false,
      }
    case 'cancelled':
      return { tone: 'neutral', headline: 'Desbloqueio cancelado. A máquina segue isolada.', released: false }
    default:
      return {
        tone: 'neutral',
        headline: `Estado "${state}" desconhecido.`,
        released: false,
      }
  }
}

/** Extrai o GUID do comando aberto da mensagem de `agent_command_already_open`. */
const GUID = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/

export function openCommandIdOf(detail: string | undefined): string | null {
  return GUID.exec(detail ?? '')?.[0] ?? null
}

/** Motivos de indisponibilidade do catálogo, em português. */
const UNAVAILABLE: Record<string, string> = {
  kind_not_deliverable:
    'Sem entrega ao agente: a máquina NÃO seria afetada.',
  command_already_open: 'Já há um comando deste tipo aberto para esta máquina.',
  agent_command_requires_admin: 'Exige o papel de administrador.',
  agent_command_requires_ip_permission:
    'Só administradores podem gerenciar IPs.',
  endpoint_has_no_agent: 'A máquina não tem agente vinculado.',
  agent_not_active: 'O agente desta máquina não está ativo.',
  tenant_inactive: 'A empresa dona desta máquina está inativa.',
}

export function unavailableReasonText(reason: string | null): string {
  if (reason === null) return 'Indisponível.'
  return UNAVAILABLE[reason] ?? reason
}
